import React, { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

export const TOUR_DONE_KEY = 'app_tour_v1_done';
const TEAL = '#29a38f';

/**
 * Lightweight guided product tour. Each step optionally targets an element by CSS
 * selector; the target is spotlighted (dim everything else) with a tooltip. Steps
 * whose target isn't on the page are skipped, so it adapts to the user's role.
 */
export default function ProductTour({ steps, run, onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [visSteps, setVisSteps] = useState(steps);

  useEffect(() => {
    if (!run) return;
    const vis = steps.filter(s => !s.selector || document.querySelector(s.selector));
    setVisSteps(vis.length ? vis : steps);
    setIdx(0);
  }, [run, steps]);

  const step = run ? visSteps[idx] : null;

  const place = useCallback(() => {
    if (!step) return;
    const el = step.selector ? document.querySelector(step.selector) : null;
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => setRect(el.getBoundingClientRect()), 180);
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => { place(); }, [place, idx]);
  useEffect(() => {
    if (!run) return;
    const onResize = () => place();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [run, place]);

  if (!run || !step) return null;

  const finish = () => {
    try { localStorage.setItem(TOUR_DONE_KEY, '1'); } catch { /* ignore */ }
    onClose?.();
  };
  const next = () => (idx < visSteps.length - 1 ? setIdx(idx + 1) : finish());
  const back = () => setIdx(Math.max(0, idx - 1));

  const pad = 8;
  let tipStyle;
  if (rect) {
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > 220 ? rect.bottom + 14 : Math.max(12, rect.top - 200);
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - 332);
    tipStyle = { top, left };
  } else {
    tipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <div className="fixed inset-0 z-[9999]">
      {rect ? (
        <div style={{
          position: 'fixed', top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)', borderRadius: 10,
          border: `2px solid ${TEAL}`, pointerEvents: 'none', transition: 'all .2s ease',
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)' }} />
      )}

      <div className="fixed w-[320px] bg-card border border-border rounded-xl shadow-2xl p-4" style={tipStyle} data-testid="product-tour">
        <button onClick={finish} className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-foreground" aria-label="Close tour">
          <X className="w-4 h-4" />
        </button>
        <div className="text-xs text-secondary font-medium mb-1">Step {idx + 1} of {visSteps.length}</div>
        <h3 className="font-semibold text-foreground mb-1 pr-4">{step.title}</h3>
        <p className="text-sm text-muted-foreground">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <button onClick={finish} className="text-xs text-muted-foreground hover:text-foreground" data-testid="tour-skip">Skip tour</button>
          <div className="flex gap-2">
            {idx > 0 && <Button size="sm" variant="outline" className="border-border" onClick={back}>Back</Button>}
            <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={next} data-testid="tour-next">
              {idx === visSteps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
